import { supabase } from '@/lib/supabase/client';
import { normalizeProofLinkWriteError } from '@/lib/customer-ops/proof-link-errors';
import type {
  ReviewDue,
  ReviewDueInsert,
  ReviewDueListFilters,
  ReviewDueStatus,
  ReviewDueUpdate,
} from '@/types/customer-ops';

function normalizeReviewDue(row: ReviewDue): ReviewDue {
  return {
    ...row,
    trigger_source: row.trigger_source ?? 'manual',
    trigger_key: row.trigger_key ?? null,
  };
}

function requiredText(value: string, fieldName: string): string {
  const trimmed = value.trim();
  if (!trimmed) {
    throw new Error(`${fieldName} is required.`);
  }
  return trimmed;
}

function nullableText(value: string | null | undefined): string | null {
  if (value === null || value === undefined) return null;
  const trimmed = value.trim();
  return trimmed || null;
}

function requiredMeetingDateForStatus(status: ReviewDueStatus, meetingDate: string | null | undefined): string | null {
  const nextMeetingDate = nullableText(meetingDate);
  if ((status === 'scheduled' || status === 'completed') && !nextMeetingDate) {
    throw new Error('Meeting date is required for scheduled or completed reviews.');
  }
  return nextMeetingDate;
}

type ReviewDueFilterQuery<T> = {
  eq(column: string, value: string): T;
  in(column: string, values: readonly string[]): T;
  lte(column: string, value: string): T;
  gte(column: string, value: string): T;
};

function applyReviewDueFilters<T extends ReviewDueFilterQuery<T>>(
  query: T,
  filters?: ReviewDueListFilters
): T {
  let next = query;
  if (!filters) return next;

  if (filters.status) {
    const statuses = Array.isArray(filters.status) ? filters.status : [filters.status];
    next = next.in('status', statuses);
  }
  if (filters.reviewType) {
    const reviewTypes = Array.isArray(filters.reviewType) ? filters.reviewType : [filters.reviewType];
    next = next.in('review_type', reviewTypes);
  }
  if (filters.locationId) {
    next = next.eq('location_id', filters.locationId);
  }
  if (filters.owner) {
    next = next.eq('owner', filters.owner);
  }
  if (filters.dueBefore) {
    next = next.lte('due_date', filters.dueBefore);
  }
  if (filters.dueAfter) {
    next = next.gte('due_date', filters.dueAfter);
  }
  return next;
}

export const reviewDueService = {
  async list(filters?: ReviewDueListFilters): Promise<ReviewDue[]> {
    try {
      let query = supabase.from('review_due').select('*').order('due_date', { ascending: true });
      query = applyReviewDueFilters(query, filters);

      const { data, error } = await query;
      if (error) {
        console.error('Error fetching review due rows:', error);
        return [];
      }
      return ((data as ReviewDue[]) || []).map(normalizeReviewDue);
    } catch (error) {
      console.error('Error fetching review due rows:', error);
      return [];
    }
  },

  async getById(id: string): Promise<ReviewDue | null> {
    try {
      const { data, error } = await supabase.from('review_due').select('*').eq('id', id).single();
      if (error) {
        if (error.code === 'PGRST116') return null;
        console.error('Error fetching review due row:', error);
        return null;
      }
      return data ? normalizeReviewDue(data as ReviewDue) : null;
    } catch (error) {
      console.error('Error fetching review due row:', error);
      return null;
    }
  },

  async create(data: ReviewDueInsert): Promise<ReviewDue> {
    const insertData = {
      location_id: requiredText(data.location_id, 'Location ID'),
      review_type: data.review_type,
      due_date: requiredText(data.due_date, 'Due date'),
      owner: nullableText(data.owner),
      status: data.status ?? 'open',
      meeting_date: data.meeting_date ?? null,
      agenda_text: data.agenda_text ?? '',
      talk_points: data.talk_points ?? '',
      proof_metric_ids: data.proof_metric_ids ?? [],
      trigger_source: data.trigger_source ?? 'manual',
      trigger_key: nullableText(data.trigger_key),
    };

    const { data: created, error } = await supabase
      .from('review_due')
      .insert(insertData)
      .select()
      .single();

    if (error) {
      const proofLinkError = normalizeProofLinkWriteError(error);
      if (proofLinkError) {
        console.error('Review due proof-link write rejected:', proofLinkError.code);
        throw proofLinkError;
      }
      console.error('Error creating review due row:', error);
      throw new Error(`Failed to create review due row: ${error.message}`);
    }
    return normalizeReviewDue(created as ReviewDue);
  },

  async update(id: string, data: ReviewDueUpdate): Promise<ReviewDue> {
    const updateData: Record<string, unknown> = {};
    if (data.review_type !== undefined) updateData.review_type = data.review_type;
    if (data.due_date !== undefined) updateData.due_date = requiredText(data.due_date, 'Due date');
    if (data.owner !== undefined) updateData.owner = nullableText(data.owner);
    if (data.status !== undefined) updateData.status = data.status;
    if (data.meeting_date !== undefined) updateData.meeting_date = data.meeting_date;
    if (data.agenda_text !== undefined) updateData.agenda_text = data.agenda_text;
    if (data.talk_points !== undefined) updateData.talk_points = data.talk_points;
    if (data.proof_metric_ids !== undefined) updateData.proof_metric_ids = data.proof_metric_ids;

    const { data: updated, error } = await supabase
      .from('review_due')
      .update(updateData)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      const proofLinkError = normalizeProofLinkWriteError(error);
      if (proofLinkError) {
        console.error('Review due proof-link write rejected:', proofLinkError.code);
        throw proofLinkError;
      }
      console.error('Error updating review due row:', error);
      throw new Error(`Failed to update review due row: ${error.message}`);
    }
    return normalizeReviewDue(updated as ReviewDue);
  },

  async setStatus(id: string, status: ReviewDueStatus, meetingDate?: string | null): Promise<ReviewDue> {
    return this.update(id, {
      status,
      meeting_date: requiredMeetingDateForStatus(status, meetingDate),
    });
  },
};
